import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Check, Link2, Minus, MoveRight, Plus, RefreshCw, RotateCw, Trash2 } from 'lucide-react';
import { prepareEmbeddedSvgMarkup, prepareInlineSvgMarkup } from '../utils/inlineSvg';
import { WIRING_GUIDE_CANVAS_FILL } from '../utils/wiringGuideSvg';

const WORKSPACE_MIN_HEIGHT = 620;
const WORKSPACE_DEFAULT_WIDTH = 1220;
const WORKSPACE_DEFAULT_HEIGHT = 760;
const BOARD_PANEL_WIDTH = 280;
const BOARD_PANEL_HEIGHT = 52;
const BOARD_PANEL_GAP = 12;
const WORKSPACE_EDGE_PADDING = 12;
const SVG_LENGTH_TO_PX = {
  px: 1,
  in: 96,
  mm: 96 / 25.4,
  cm: 96 / 2.54,
  pt: 96 / 72,
  pc: 16,
  q: 96 / 101.6,
};

const EditorShell = styled.div`
  display: grid;
  gap: 20px;
`;

const WorkspaceZoomLayer = styled.div`
   position: absolute;
   inset: 0;
   transform-origin: top left;
`;

const InstructionsCard = styled.div`
  border: 1px solid #dbeafe;
  background: linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%);
  border-radius: 12px;
  padding: 16px 18px;
  display: grid;
  gap: 10px;
`;

const InstructionsTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const InstructionsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1d4ed8;
`;

const InstructionsText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #475569;
`;

const ResetButton = styled.button`
  border: 1px solid #bfdbfe;
  background: white;
  color: #1d4ed8;
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const BoardControlCard = styled.div`
  border: 1px solid ${(props) => props.$emphasized ? '#bfdbfe' : '#dbe4f0'};
  border-radius: 14px;
  background: ${(props) => props.$emphasized
    ? 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)'
    : 'white'};
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
  box-shadow: ${(props) => props.$emphasized
    ? '0 12px 28px -24px rgba(37, 99, 235, 0.42)'
    : 'none'};
`;

const BoardControlMeta = styled.div`
  min-width: 0;
  display: grid;
  gap: 3px;
`;

const BoardControlLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
`;

const BoardControlTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BoardControlHint = styled.div`
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BoardControlActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const SuggestionPrimaryButton = styled.button`
  min-height: 40px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid #2563eb;
  background: ${(props) => props.$active
    ? 'linear-gradient(180deg, #dbeafe 0%, #bfdbfe 100%)'
    : 'linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)'};
  color: ${(props) => props.$active ? '#1e3a8a' : '#ffffff'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.01em;
  box-shadow: ${(props) => props.$active
    ? '0 10px 24px -18px rgba(37, 99, 235, 0.48)'
    : '0 12px 24px -16px rgba(37, 99, 235, 0.55)'};
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.02);
    box-shadow: 0 14px 28px -18px rgba(37, 99, 235, 0.6);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ControlButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #dbe4f0;
  background: white;
  color: #334155;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #93c5fd;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuggestionApplyButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 11px;
  border: 1px solid #10b981;
  background: #10b981;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    background: #059669;
    border-color: #059669;
    color: #ffffff;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ScaleChip = styled.div`
  min-width: 64px;
  padding: 8px 10px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
`;

const Workspace = styled.div`
  position: relative;
  min-height: ${WORKSPACE_MIN_HEIGHT}px;
  height: clamp(${WORKSPACE_MIN_HEIGHT}px, 72vh, 860px);
  border: 1px solid #dbe4f0;
  border-radius: 18px;
  overflow: hidden;
  touch-action: none;
  background:
    radial-gradient(circle at 1px 1px, rgba(148, 163, 184, 0.16) 1px, transparent 0),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  background-size: 24px 24px, auto;
`;

const BoardSprite = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  user-select: none;
  touch-action: none;
  cursor: ${(props) => (props.$dragging ? 'grabbing' : 'grab')};
  filter: ${(props) => (props.$active ? 'drop-shadow(0 14px 28px rgba(37, 99, 235, 0.18))' : 'drop-shadow(0 10px 18px rgba(15, 23, 42, 0.12))')};
`;

const RotatedBoardFrame = styled.div`
  position: absolute;
  transform-origin: center center;
`;

const BoardSvgHost = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  > svg {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
  }
`;

const BoardFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.85);
`;

const WorkspaceToolbar = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 24;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 999px;
  border: 1px solid rgba(191, 219, 254, 0.96);
  background: rgba(255, 255, 255, 0.96);
  pointer-events: auto;
  box-shadow: 0 14px 24px -18px rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(10px);
`;

const BoardFloatingPanel = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  min-height: ${BOARD_PANEL_HEIGHT}px;
  border-radius: 16px;
  border: 1px solid rgba(191, 219, 254, 0.96);
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 18px 28px -22px rgba(15, 23, 42, 0.88);
  backdrop-filter: blur(10px);
  pointer-events: auto;
`;

const BoardFloatingName = styled.div`
  min-width: 0;
  padding: 0 2px;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BoardFloatingActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const FloatingScaleChip = styled(ScaleChip)`
  min-width: 58px;
  cursor: default;
`;

const FloatingControlButton = styled(ControlButton)`
  width: 32px;
  height: 32px;

  &:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Overlay = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  z-index: 10;
  pointer-events: none;
`;

const ConnectionsCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: white;
  overflow: hidden;
`;

const ConnectionsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const ConnectionsTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
`;

const ConnectionsCount = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
`;

const EmptyState = styled.div`
  padding: 18px;
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
`;

const ConnectionList = styled.div`
  display: grid;
`;

const ConnectionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto auto;
  gap: 12px;
  align-items: center;
  padding: 14px 18px;
  border-top: 1px solid #f1f5f9;

  @media (max-width: 720px) {
    grid-template-columns: 1fr auto;
  }
`;

const ConnectionPin = styled.div`
  min-width: 0;
  display: grid;
  gap: 2px;
`;

const ConnectionPinName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ConnectionPinMeta = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const ConnectionArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;

  @media (max-width: 720px) {
    display: none;
  }
`;

const RemoveButton = styled.button`
  width: 34px;
  height: 34px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #dc2626;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const BOARD_EDITOR_SVG_CSS = `
  [data-micropinouts-callout="1"],
  [data-micropinouts-callout-style="1"] {
    display: none !important;
  }

  .pin-hole,
  .pin-rect {
    cursor: crosshair !important;
    fill: rgba(255, 255, 255, 0.96) !important;
    stroke: #475569 !important;
    stroke-width: 1.8 !important;
    transition: fill 0.15s ease, stroke 0.15s ease, opacity 0.15s ease !important;
    pointer-events: all !important;
  }

  .pin-hole:hover,
  .pin-rect:hover {
    fill: rgba(37, 99, 235, 0.14) !important;
    stroke: #2563eb !important;
    stroke-width: 2.6 !important;
    opacity: 1 !important;
  }

  svg {
    overflow: visible !important;
  }
`;

const BOARD_EXPORT_SVG_CSS = `
  [data-micropinouts-callout="1"],
  [data-micropinouts-callout-style="1"] {
    display: none !important;
  }

  .pin-hole,
  .pin-rect {
    fill: rgba(255, 255, 255, 0.96) !important;
    stroke: #475569 !important;
    stroke-width: 1.8 !important;
  }
`;

const CONNECTION_COLORS = [
  '#2563eb',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#ef4444',
  '#06b6d4',
  '#84cc16',
  '#f97316',
  '#ec4899',
  '#6366f1',
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Auto Wiring Compatibility Functions
const parseVoltageRange = (voltageStr) => {
  if (!voltageStr) return null;
  const match = voltageStr.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
  if (match) {
    return { min: parseFloat(match[1]), max: parseFloat(match[2]) };
  }
  const single = parseFloat(voltageStr.replace(/[^\d.]/g, ''));
  return Number.isFinite(single) ? { min: single, max: single } : null;
};

const areVoltagesCompatible = (sensorPin, microcontrollerPin) => {
  const sensorVoltage = parseVoltageRange(getPinValue(sensorPin, ['voltage_range']));
  const microVoltage = parseVoltageRange(getPinValue(microcontrollerPin, ['voltage_range']));

  if (!sensorVoltage || !microVoltage) return true; // Assume compatible if no voltage info

  // Check if voltage ranges overlap
  return sensorVoltage.max >= microVoltage.min && microVoltage.max >= sensorVoltage.min;
};

const areCurrentsCompatible = (sensorPin, microcontrollerPin) => {
  const sensorCurrent = parseFloat(getPinValue(sensorPin, ['current_limit_ma', 'current_limit']));
  const microCurrent = parseFloat(getPinValue(microcontrollerPin, ['current_limit_ma', 'current_limit']));

  if (!Number.isFinite(sensorCurrent) || !Number.isFinite(microCurrent)) return true;

  // Sensor should not draw more current than microcontroller pin can provide
  return sensorCurrent <= microCurrent;
};

const normalizePinRole = (role) => {
  const normalized = String(role || '').trim().toLowerCase();
  if (!normalized) return '';

  if (normalized.includes('power')) return 'power';
  if (normalized === 'gnd' || normalized === 'ground') return 'ground';
  if (normalized === 'gpio' || normalized === 'digital') return 'digital';
  if (normalized === 'analog-in' || normalized === 'adc' || normalized === 'ain') return 'analog';
  if (normalized === 'analog-out' || normalized === 'dac' || normalized === 'aout') return 'analog';
  if (normalized.startsWith('i2c-')) return normalized;
  if (normalized.startsWith('spi-')) return normalized;
  if (normalized.startsWith('uart-')) return normalized;
  if (normalized === 'interrupt' || normalized === 'chip-enable') return normalized;

  return normalized;
};

const getPinText = (pin) => [
  getPinValue(pin, ['pin_role']),
  getPinValue(pin, ['functions']),
  getPinValue(pin, ['alternate_functions']),
  getPinValue(pin, ['description']),
  getPinValue(pin, ['pin_name'])
]
  .filter(Boolean)
  .join(' ')
  .toLowerCase();

const inferPinRole = (pin) => {
  const text = getPinText(pin);
  const pinName = String(getPinValue(pin, ['pin_name'])).trim().toLowerCase();

  if (pinName === 'sda' || /\bsda\b/.test(text)) return 'i2c-sda';
  if (pinName === 'scl' || /\bscl\b/.test(text)) return 'i2c-scl';
  if (pinName === 'mosi' || /\bmosi\b/.test(text) || /\bsdi\b/.test(text)) return 'spi-mosi';
  if (pinName === 'miso' || /\bmiso\b/.test(text) || /\bsdo\b/.test(text)) return 'spi-miso';
  if (pinName === 'sck' || pinName === 'clk' || /\bsck\b/.test(text) || /\bsclk\b/.test(text) || /\bclk\b/.test(text)) return 'spi-sck';
  if (/\bcsb\b/.test(text) || /\bss\b/.test(text) || /\bchip select\b/.test(text) || /\bcs\b/.test(text)) return 'spi-cs';
  if (pinName === 'tx' || /\btx\b/.test(text)) return 'uart-tx';
  if (pinName === 'rx' || /\brx\b/.test(text)) return 'uart-rx';
  if (/\bi2c\b/.test(text)) return 'i2c';
  if (/\bspi\b/.test(text)) return 'spi';
  if (/\buart\b|\bserial\b/.test(text)) return 'uart';
  if (/\banalog\s*in\b|\badc\b|\bain\b/.test(text)) return 'analog';
  if (/\banalog\s*out\b|\bdac\b|\baout\b/.test(text)) return 'analog';
  if (/\bpwm\b/.test(text)) return 'pwm';
  if (/\binterrupt\b/.test(text)) return 'interrupt';
  if (/\b(gnd|ground)\b/.test(text)) return 'ground';
  if (/\b(vcc|vin|vdd|3\.3v|3v3|5v)\b/.test(text)) return 'power';
  if (/\bgpio\b|\bdigital\b/.test(text)) return 'digital';
  return '';
};

const getPinRole = (pin) => {
  const explicitRole = normalizePinRole(getPinValue(pin, ['pin_role']));
  if (explicitRole) return explicitRole;
  return normalizePinRole(inferPinRole(pin));
};

const isSignalRole = (role) => {
  const normalized = String(role || '').trim().toLowerCase();

  return ![
    'power',
    'ground',
    'gnd',
    'vcc',
    'vin',
    '3v3',
    '3.3v',
    '5v'
  ].includes(normalized);
};

const getPinCapabilities = (pin) => {
  const tags = pin?.capability_tags ?? pin?.capabilityTags ?? '';

  if (Array.isArray(tags)) {
    return tags.map(normalizePinRole).filter(Boolean);
  }

  const text = String(tags || '').trim();
  if (!text) return [];

  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.map(normalizePinRole).filter(Boolean);
    }
  } catch {
    // allow CSV fallback
  }

  return text
    .split(',')
    .map(normalizePinRole)
    .filter(Boolean);
};

const getPreferredConnectionRole = (pin) => {
  const caps = getPinCapabilities(pin);

  const priority = [
    'i2c-sda',
    'i2c-scl',
    'spi-mosi',
    'spi-miso',
    'spi-sck',
    'spi-cs',
    'uart-tx',
    'uart-rx',
    'analog',
    'pwm',
    'interrupt',
    'digital'
  ];

  return priority.find((role) => caps.includes(role)) || getPinRole(pin);
};

const arePinsCompatible = (sensorPin, microcontrollerPin) => {
  const sensorRole = getPreferredConnectionRole(sensorPin);
  const microRole = getPreferredConnectionRole(microcontrollerPin);
  const microCapabilities = getPinCapabilities(microcontrollerPin);

  const microSupportsRole = (role) => {
    const normalizedRole = normalizePinRole(role);

    if (microRole === normalizedRole) return true;
    if (microCapabilities.includes(normalizedRole)) return true;

    return false;
  };

  // Basic role compatibility matrix
  const compatibilityMatrix = {
    digital: ['digital', 'gpio', 'i2c', 'spi', 'uart', 'pwm', 'interrupt'],
    gpio: ['gpio', 'digital', 'i2c', 'spi', 'uart', 'pwm', 'interrupt'],
    analog: ['analog', 'adc', 'dac', 'digital', 'gpio'],
    power: ['power', 'vcc'],
    ground: ['ground', 'gnd'],
    i2c: ['i2c', 'i2c-sda', 'i2c-scl'],
    'i2c-sda': ['i2c-sda'],
    'i2c-scl': ['i2c-scl'],
    spi: ['spi', 'spi-mosi', 'spi-miso', 'spi-sck', 'spi-cs'],
    'spi-mosi': ['spi-mosi'],
    'spi-miso': ['spi-miso'],
    'spi-sck': ['spi-sck'],
    'spi-cs': ['spi-cs', 'digital', 'gpio'],
    uart: ['uart', 'uart-tx', 'uart-rx'],
    'uart-tx': ['uart-rx'],
    'uart-rx': ['uart-tx'],
    pwm: ['pwm', 'digital', 'gpio'],
    interrupt: ['interrupt', 'digital', 'gpio']
  };

  // Check if roles are compatible
  const compatibleRoles = compatibilityMatrix[sensorRole] || [];
  if (
    !compatibleRoles.includes(microRole) &&
    !compatibleRoles.some(role => microSupportsRole(role))
  ) {
    return false;
  }

  // Check voltage compatibility
  if (!areVoltagesCompatible(sensorPin, microcontrollerPin)) {
    return false;
  }

  // Check current compatibility
  if (!areCurrentsCompatible(sensorPin, microcontrollerPin)) {
    return false;
  }

  // Check 5V tolerance only for signal-level connections
  const is5vTolerant = getPinValue(microcontrollerPin, ['is_5v_tolerant']) === '1';
  const sensorVoltage = parseVoltageRange(getPinValue(sensorPin, ['voltage_range']));
  if (
    isSignalRole(sensorRole) &&
    isSignalRole(microRole) &&
    sensorVoltage &&
    sensorVoltage.max > 3.3 &&
    !is5vTolerant
  ) {
    return false;
  }

  return true;
};

const calculateConnectionScore = (sensorPin, microcontrollerPin) => {
  let score = 0;

  const sensorRole = getPreferredConnectionRole(sensorPin);
  const microRole = getPreferredConnectionRole(microcontrollerPin);

  if (sensorRole === microRole) {
    score += 100;
  }
  // Exact role match gets highest score
  if (getPinRole(sensorPin) === getPinRole(microcontrollerPin)) {
    score += 100;
  }

  // Protocol-specific matches
  const sensorCapabilities = getPinCapabilities(sensorPin);
  const microCapabilities = getPinCapabilities(microcontrollerPin);

  if (sensorCapabilities.includes('i2c') && microCapabilities.includes('i2c')) {
    score += 50;
  }
  if (sensorCapabilities.includes('spi') && microCapabilities.includes('spi')) {
    score += 50;
  }
  if (sensorCapabilities.includes('uart') && microCapabilities.includes('uart')) {
    score += 50;
  }

  // Voltage compatibility bonus
  if (areVoltagesCompatible(sensorPin, microcontrollerPin)) {
    score += 20;
  }

  // Current compatibility bonus
  if (areCurrentsCompatible(sensorPin, microcontrollerPin)) {
    score += 20;
  }

  return score;
};

const getConnectionConfidence = (sensorPin, microcontrollerPin) => {
  if (!sensorPin || !microcontrollerPin) {
    return 'unknown';
  }

  if (!arePinsCompatible(sensorPin, microcontrollerPin)) {
    return 'low';
  }

  const score = calculateConnectionScore(sensorPin, microcontrollerPin);

  return score > 50 ? 'high' : score > 20 ? 'medium' : 'low';
};

const generateAutoWiringSuggestions = (sensorPins, microcontrollerPins) => {
  const suggestions = [];
  const usedMicroPins = new Set();
  const usedSensorPins = new Set();

  // Sort pins by priority (power/ground first, then specific protocols, then general IO)
  const sortByPriority = (pins) => {
    return [...pins].sort((a, b) => {
      const roleA = getPinRole(a);
      const roleB = getPinRole(b);

      const priorityOrder = ['power', 'ground', 'gnd', 'vcc', 'i2c', 'spi', 'uart', 'analog', 'digital', 'gpio'];
      const priorityA = priorityOrder.indexOf(roleA);
      const priorityB = priorityOrder.indexOf(roleB);

      if (priorityA !== -1 && priorityB !== -1) {
        return priorityA - priorityB;
      }
      if (priorityA !== -1) return -1;
      if (priorityB !== -1) return 1;
      return 0;
    });
  };

  const sortedSensorPins = sortByPriority(sensorPins);
  const sortedMicroPins = sortByPriority(microcontrollerPins);

  // Generate suggestions
  for (const sensorPin of sortedSensorPins) {
    if (usedSensorPins.has(sensorPin.id)) continue;

    let bestMatch = null;
    let bestScore = 0;

    for (const microPin of sortedMicroPins) {
      if (usedMicroPins.has(microPin.id)) continue;

      if (arePinsCompatible(sensorPin, microPin)) {
        const score = calculateConnectionScore(sensorPin, microPin);
        if (score > bestScore) {
          bestScore = score;
          bestMatch = microPin;
        }
      }
    }

    if (bestMatch && bestScore > 0) {
      suggestions.push({
        sensorPin: sensorPin.id,
        microcontrollerPin: bestMatch.id,
        score: bestScore,
        confidence: bestScore > 50 ? 'high' : bestScore > 20 ? 'medium' : 'low'
      });

      usedSensorPins.add(sensorPin.id);
      usedMicroPins.add(bestMatch.id);
    }
  }

  return suggestions.sort((a, b) => b.score - a.score);
};

const getPinValue = (pin, keys) => {
  for (const key of keys) {
    const value = pin?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }

  return '';
};

const parseSvgLength = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return null;
  }

  const match = text.match(/^([+-]?\d*\.?\d+)([a-z%]*)$/i);
  if (!match) {
    return null;
  }

  const amount = Number.parseFloat(match[1]);
  const unit = String(match[2] || 'px').toLowerCase();
  if (!Number.isFinite(amount)) {
    return null;
  }

  return {
    value: amount,
    unit,
    px: SVG_LENGTH_TO_PX[unit] ? amount * SVG_LENGTH_TO_PX[unit] : null,
  };
};

const parseViewBox = (value) => {
  const parts = String(value || '')
    .trim()
    .split(/[\s,]+/)
    .map((segment) => Number.parseFloat(segment))
    .filter((segment) => Number.isFinite(segment));

  if (parts.length >= 4) {
    return {
      minX: parts[0],
      minY: parts[1],
      width: parts[2],
      height: parts[3],
    };
  }

  return null;
};

const parseSvgNumericValue = (value) => {
  const parsedLength = parseSvgLength(value);
  if (parsedLength?.px) {
    return parsedLength.px;
  }

  if (parsedLength?.value) {
    return parsedLength.value;
  }

  const numeric = Number.parseFloat(String(value || '').trim());
  return Number.isFinite(numeric) ? numeric : null;
};

const parseSvgNumericAttribute = (element, attributeName) => {
  if (!(element instanceof Element)) {
    return null;
  }

  return parseSvgNumericValue(element.getAttribute(attributeName) || '');
};

const SVG_CALL_OUT_SELECTOR = '[data-micropinouts-callout="1"], [data-micropinouts-callout-style="1"]';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

const parseSvgRootElement = (markup) => {
  const rawMarkup = String(markup || '').trim();
  if (!rawMarkup || typeof DOMParser === 'undefined') {
    return null;
  }

  try {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(rawMarkup, 'image/svg+xml');
    const parseError = parsedDocument.querySelector('parsererror');
    const svg = parsedDocument.documentElement;

    if (!parseError && svg && svg.nodeName.toLowerCase().includes('svg')) {
      return svg;
    }
  } catch (error) {
    return null;
  }

  return null;
};

const copySvgViewportAttributes = (targetSvg, sourceSvg) => {
  if (!(targetSvg instanceof Element) || !(sourceSvg instanceof Element)) {
    return false;
  }

  let appliedAnyAttribute = false;
  ['viewBox', 'width', 'height'].forEach((attributeName) => {
    if (sourceSvg.hasAttribute(attributeName)) {
      targetSvg.setAttribute(attributeName, sourceSvg.getAttribute(attributeName) || '');
      appliedAnyAttribute = true;
    } else {
      targetSvg.removeAttribute(attributeName);
    }
  });

  return appliedAnyAttribute;
};

const measureSvgContentBounds = (markup) => {
  const rawMarkup = String(markup || '').trim();
  if (!rawMarkup || typeof document === 'undefined' || !document.body) {
    return null;
  }

  const tempHost = document.createElement('div');
  tempHost.setAttribute('aria-hidden', 'true');
  tempHost.style.cssText = 'position:absolute;left:-100000px;top:-100000px;opacity:0;pointer-events:none;overflow:hidden;';
  tempHost.innerHTML = rawMarkup;

  const svg = tempHost.querySelector('svg');
  if (!(svg instanceof SVGElement)) {
    tempHost.remove();
    return null;
  }

  const measureGroup = document.createElementNS(SVG_NAMESPACE, 'g');
  const preserveTags = new Set(['defs', 'style', 'title', 'desc', 'metadata']);

  Array.from(svg.childNodes).forEach((child) => {
    if (child.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    if (preserveTags.has(child.nodeName.toLowerCase())) {
      return;
    }

    measureGroup.appendChild(child.cloneNode(true));
  });

  if (!measureGroup.childNodes.length) {
    tempHost.remove();
    return null;
  }

  svg.appendChild(measureGroup);
  document.body.appendChild(tempHost);

  try {
    const box = measureGroup.getBBox();
    if (!Number.isFinite(box.x) || !Number.isFinite(box.y) || box.width <= 0 || box.height <= 0) {
      return null;
    }

    return {
      minX: Number(box.x.toFixed(2)),
      minY: Number(box.y.toFixed(2)),
      width: Number(box.width.toFixed(2)),
      height: Number(box.height.toFixed(2)),
    };
  } catch (error) {
    return null;
  } finally {
    tempHost.remove();
  }
};

const stripBoardCalloutsFromSvg = (renderedSvgContent, sourceSvgContent = '') => {
  const renderedMarkup = String(renderedSvgContent || '').trim();
  if (!renderedMarkup) {
    return '';
  }

  if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
    return renderedMarkup;
  }

  try {
    const parser = new DOMParser();
    const renderedDocument = parser.parseFromString(renderedMarkup, 'image/svg+xml');
    const parseError = renderedDocument.querySelector('parsererror');
    const renderedSvg = renderedDocument.documentElement;

    if (parseError || !renderedSvg || !renderedSvg.nodeName.toLowerCase().includes('svg')) {
      return renderedMarkup;
    }

    renderedSvg.querySelectorAll(SVG_CALL_OUT_SELECTOR).forEach((node) => {
      node.remove();
    });

    const sourceSvg = parseSvgRootElement(sourceSvgContent);
    const restoredViewport = copySvgViewportAttributes(renderedSvg, sourceSvg);

    if (!restoredViewport) {
      const sanitizedMarkup = new XMLSerializer().serializeToString(renderedSvg);
      const measuredBounds = measureSvgContentBounds(sanitizedMarkup);

      if (measuredBounds) {
        renderedSvg.setAttribute('viewBox', `${measuredBounds.minX} ${measuredBounds.minY} ${measuredBounds.width} ${measuredBounds.height}`);
        renderedSvg.setAttribute('width', String(measuredBounds.width));
        renderedSvg.setAttribute('height', String(measuredBounds.height));
      }
    }

    return new XMLSerializer().serializeToString(renderedSvg);
  } catch (error) {
    return renderedMarkup;
  }
};

const parseTransformOperation = (transformValue, operationName) => {
  const match = String(transformValue || '').match(new RegExp(`${operationName}\\(([^)]+)\\)`, 'i'));
  if (!match?.[1]) {
    return null;
  }

  const values = match[1]
    .trim()
    .split(/[\s,]+/)
    .map((segment) => Number.parseFloat(segment))
    .filter((segment) => Number.isFinite(segment));

  return values.length > 0 ? values : null;
};

const getDirectChildSvgElement = (element) => {
  if (!(element instanceof Element)) {
    return null;
  }

  return Array.from(element.children).find((child) => child.nodeName.toLowerCase() === 'svg') || null;
};

const parseSavedBoardPlacement = (element) => {
  if (!(element instanceof Element)) {
    return null;
  }

  const tagName = element.nodeName.toLowerCase();

  if (tagName === 'svg') {
    const x = parseSvgNumericAttribute(element, 'x');
    const y = parseSvgNumericAttribute(element, 'y');
    const width = parseSvgNumericAttribute(element, 'width');
    const height = parseSvgNumericAttribute(element, 'height');

    if ([x, y, width, height].every((value) => Number.isFinite(value))) {
      return {
        x,
        y,
        rawWidth: width,
        rawHeight: height,
        rotation: 0,
        scaleFactor: null,
      };
    }

    return null;
  }

  if (tagName !== 'g') {
    return null;
  }

  const transformValue = element.getAttribute('transform') || '';
  const nestedSvg = getDirectChildSvgElement(element);

  if (nestedSvg) {
    const x = parseSvgNumericAttribute(nestedSvg, 'x');
    const y = parseSvgNumericAttribute(nestedSvg, 'y');
    const width = parseSvgNumericAttribute(nestedSvg, 'width');
    const height = parseSvgNumericAttribute(nestedSvg, 'height');
    const rotateValues = parseTransformOperation(transformValue, 'rotate');

    if ([x, y, width, height].every((value) => Number.isFinite(value))) {
      return {
        x,
        y,
        rawWidth: width,
        rawHeight: height,
        rotation: rotateValues?.[0] || 0,
        scaleFactor: null,
      };
    }
  }

  const translateValues = parseTransformOperation(transformValue, 'translate');
  const scaleValues = parseTransformOperation(transformValue, 'scale');
  if (!translateValues || translateValues.length === 0) {
    return null;
  }

  return {
    x: translateValues[0] || 0,
    y: translateValues[1] ?? 0,
    rawWidth: null,
    rawHeight: null,
    rotation: 0,
    scaleFactor: scaleValues?.[0] || 1,
  };
};

const parseSavedDiagramSnapshot = (rawSvgContent) => {
  const markup = String(rawSvgContent || '').trim();
  if (!markup || typeof DOMParser === 'undefined') {
    return null;
  }

  try {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(markup, 'image/svg+xml');
    if (parsedDocument.querySelector('parsererror')) {
      return null;
    }

    const root = parsedDocument.documentElement;
    if (!root || root.nodeName.toLowerCase() !== 'svg') {
      return null;
    }

    const viewBox = parseViewBox(root.getAttribute('viewBox') || '');
    const canvasWidth = parseSvgNumericAttribute(root, 'width') || viewBox?.width || null;
    const canvasHeight = parseSvgNumericAttribute(root, 'height') || viewBox?.height || null;
    const placements = [];

    Array.from(root.children).some((child) => {
      const placement = parseSavedBoardPlacement(child);
      if (!placement) {
        return false;
      }

      placements.push(placement);
      return placements.length >= 2;
    });

    if (placements.length < 2) {
      return null;
    }

    return {
      canvasWidth: Number.isFinite(canvasWidth) && canvasWidth > 0 ? canvasWidth : null,
      canvasHeight: Number.isFinite(canvasHeight) && canvasHeight > 0 ? canvasHeight : null,
      sensor: placements[0],
      microcontroller: placements[1],
    };
  } catch (error) {
    return null;
  }
};

const getSourceSvgContent = (board) => String(
  board?.svg_source_content
  || board?.svgSourceContent
  || ''
).trim();

const getRenderedSvgContent = (board) => String(board?.svg_content || board?.svgContent || '').trim();

const parseSvgMetrics = (rawSvgContent) => {
  const fallback = {
    width: 260,
    height: 180,
    label: 'Fallback size',
  };

  const markup = String(rawSvgContent || '').trim();
  if (!markup) {
    return fallback;
  }

  let widthValue = '';
  let heightValue = '';
  let viewBoxValue = '';

  if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(markup, 'image/svg+xml');
      const parseError = parsedDocument.querySelector('parsererror');
      const svg = parsedDocument.documentElement;

      if (!parseError && svg && svg.nodeName.toLowerCase().includes('svg')) {
        widthValue = svg.getAttribute('width') || '';
        heightValue = svg.getAttribute('height') || '';
        viewBoxValue = svg.getAttribute('viewBox') || '';
      }
    } catch (error) {
      // Fallback to regex below.
    }
  }

  if (!widthValue || !heightValue) {
    const widthMatch = markup.match(/\bwidth=["']([^"']+)["']/i);
    const heightMatch = markup.match(/\bheight=["']([^"']+)["']/i);
    widthValue = widthValue || widthMatch?.[1] || '';
    heightValue = heightValue || heightMatch?.[1] || '';
  }

  if (!viewBoxValue) {
    viewBoxValue = markup.match(/\bviewBox=["']([^"']+)["']/i)?.[1] || '';
  }

  const widthInfo = parseSvgLength(widthValue);
  const heightInfo = parseSvgLength(heightValue);
  const viewBox = parseViewBox(viewBoxValue);

  if (widthInfo?.px && heightInfo?.px) {
    const preferredUnit = widthInfo.unit === heightInfo.unit ? widthInfo.unit : 'svg-units';
    return {
      width: Math.max(widthInfo.px, 1),
      height: Math.max(heightInfo.px, 1),
      label: preferredUnit === 'px' ? 'SVG width/height scale' : `SVG ${preferredUnit} scale`,
    };
  }

  if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
    return {
      width: viewBox.width,
      height: viewBox.height,
      label: 'SVG viewBox scale',
    };
  }

  if (widthInfo?.value && heightInfo?.value) {
    return {
      width: Math.max(widthInfo.value, 1),
      height: Math.max(heightInfo.value, 1),
      label: 'SVG attribute scale',
    };
  }

  return fallback;
};

const normalizeRotation = (value) => {
  const normalized = ((Number(value) || 0) % 360 + 360) % 360;
  if (normalized >= 315 || normalized < 45) {
    return 0;
  }
  if (normalized < 135) {
    return 90;
  }
  if (normalized < 225) {
    return 180;
  }
  return 270;
};

const getRotatedSize = (width, height, rotation) => {
  const normalizedRotation = normalizeRotation(rotation);
  if (normalizedRotation === 90 || normalizedRotation === 270) {
    return { width: height, height: width };
  }

  return { width, height };
};

/* const getBoardFootprint = (metric, rotation, commonScale, scaleAdjustment) => {
  const scale = Math.max(0.35, Number(scaleAdjustment) || 1);
  const baseWidth = Math.max(40, Number((metric.width * commonScale * scale).toFixed(2)));
  const baseHeight = Math.max(40, Number((metric.height * commonScale * scale).toFixed(2)));
  const rotated = getRotatedSize(baseWidth, baseHeight, rotation);

  return {
    baseWidth,
    baseHeight,
    renderWidth: rotated.width,
    renderHeight: rotated.height,
    innerOffsetX: (rotated.width - baseWidth) / 2,
    innerOffsetY: (rotated.height - baseHeight) / 2,
  };
}; */

const getBoardFootprint = (metric, rotation, commonScale, scaleAdjustment, side = 'sensor') => {
  const scale = Math.max(0.35, Number(scaleAdjustment) || 1);

  const targetWidth = side === 'microcontroller' ? 180 : 130;
  const aspectRatio = metric.height / metric.width;

  const baseWidth = targetWidth * scale;
  const baseHeight = baseWidth * aspectRatio;

  const rotated = getRotatedSize(baseWidth, baseHeight, rotation);

  return {
    baseWidth,
    baseHeight,
    renderWidth: rotated.width,
    renderHeight: rotated.height,
    innerOffsetX: (rotated.width - baseWidth) / 2,
    innerOffsetY: (rotated.height - baseHeight) / 2,
  };
};

const buildRestoredBoardState = (snapshot, boardMetrics, commonScale, workspaceSize) => {
  if (!snapshot) {
    return null;
  }

  const workspaceWidth = Number(workspaceSize?.width) || WORKSPACE_DEFAULT_WIDTH;
  const workspaceHeight = Number(workspaceSize?.height) || WORKSPACE_DEFAULT_HEIGHT;
  const snapshotWidth = Math.max(1, Number(snapshot.canvasWidth) || workspaceWidth);
  const snapshotHeight = Math.max(1, Number(snapshot.canvasHeight) || workspaceHeight);
  const availableWidth = Math.max(260, workspaceWidth - 96);
  const availableHeight = Math.max(220, workspaceHeight - 96);
  const diagramScale = clamp(Math.min(availableWidth / snapshotWidth, availableHeight / snapshotHeight, 1), 0.18, 1);
  const offsetX = Math.max(24, (workspaceWidth - (snapshotWidth * diagramScale)) / 2);
  const offsetY = Math.max(24, (workspaceHeight - (snapshotHeight * diagramScale)) / 2);
  const rotations = {};
  const scaleAdjustments = {};
  const layouts = {};

  for (const side of ['sensor', 'microcontroller']) {
    const placement = snapshot[side];
    const metric = boardMetrics?.[side];
    if (!placement || !metric) {
      return null;
    }

    const rotation = normalizeRotation(placement.rotation);
    const rawWidth = Number.isFinite(placement.rawWidth) && placement.rawWidth > 0
      ? placement.rawWidth
      : Math.max(metric.width * Math.max(Number(placement.scaleFactor) || 1, 0.1), 40);
    const targetBaseWidth = Math.max(40, rawWidth * diagramScale);
    const defaultBaseWidth = Math.max(
      getBoardFootprint(metric, rotation, commonScale, 1, side).baseWidth,
      1
    );
    const scaleAdjustment = clamp(Number((targetBaseWidth / defaultBaseWidth).toFixed(4)), 0.45, 2.4);
    const footprint = getBoardFootprint(metric, rotation, commonScale, scaleAdjustment, side);
    const placedX = offsetX + ((Number(placement.x) || 0) * diagramScale);
    const placedY = offsetY + ((Number(placement.y) || 0) * diagramScale);

    rotations[side] = rotation;
    scaleAdjustments[side] = scaleAdjustment;
    layouts[side] = clampBoardPosition({
      x: placedX - footprint.innerOffsetX,
      y: placedY - footprint.innerOffsetY,
    }, footprint, workspaceSize);
  }

  return {
    layouts,
    rotations,
    scaleAdjustments,
  };
};

const clampBoardPosition = (position, footprint, workspaceSize) => {
  if (!position || !footprint) {
    return position;
  }

  const width = Number(workspaceSize?.width) || WORKSPACE_DEFAULT_WIDTH;
  const height = Number(workspaceSize?.height) || WORKSPACE_DEFAULT_HEIGHT;
  const overflowX = Math.max(36, footprint.renderWidth * 0.28);
  const overflowY = Math.max(36, footprint.renderHeight * 0.28);

  return {
    x: clamp(Number(position.x) || 0, -footprint.renderWidth + overflowX, width - overflowX),
    y: clamp(Number(position.y) || 0, -footprint.renderHeight + overflowY, height - overflowY),
  };
};

const positionsMatch = (firstPosition, secondPosition) => (
  Number(firstPosition?.x) === Number(secondPosition?.x)
  && Number(firstPosition?.y) === Number(secondPosition?.y)
);

const doesElementMatchPin = (element, pin) => {
  if (!(element instanceof Element) || !pin) {
    return false;
  }

  const elementSvgId = String(element.getAttribute('data-svgid') || '').trim();
  const pinSvgId = getPinValue(pin, ['svg_id', 'svgId']);
  if (elementSvgId && pinSvgId && elementSvgId === pinSvgId) {
    return true;
  }

  const elementPinNumber = String(element.getAttribute('data-pin-number') || '').trim();
  const pinNumber = getPinValue(pin, ['pin_number', 'pinNumber']);
  if (elementPinNumber && pinNumber && elementPinNumber === pinNumber) {
    return true;
  }

  const elementPinName = String(element.getAttribute('data-pin') || '').trim().toLowerCase();
  const pinName = getPinValue(pin, ['pin_name', 'pinName']).toLowerCase();
  if (elementPinName && pinName && elementPinName === pinName) {
    return true;
  }

  return false;
};

const findPinForElement = (pins, element) => {
  if (!(element instanceof Element)) {
    return null;
  }

  return (Array.isArray(pins) ? pins : []).find((pin) => doesElementMatchPin(element, pin)) || null;
};

const getElementMarkerKey = (element) => String(element?.getAttribute?.('data-wire-marker-key') || '').trim();

const getElementAssignedPinId = (element) => String(element?.getAttribute?.('data-wire-pin-id') || '').trim();

const getPinMatchCandidatesForElement = (pins, element) => {
  const safePins = Array.isArray(pins) ? pins : [];
  if (!(element instanceof Element) || safePins.length === 0) {
    return [];
  }

  const elementSvgId = String(element.getAttribute('data-svgid') || '').trim();
  if (elementSvgId) {
    const svgMatches = safePins.filter((pin) => getPinValue(pin, ['svg_id', 'svgId']) === elementSvgId);
    if (svgMatches.length > 0) {
      return svgMatches;
    }
  }

  const elementPinNumber = String(element.getAttribute('data-pin-number') || '').trim();
  if (elementPinNumber) {
    const pinNumberMatches = safePins.filter((pin) => getPinValue(pin, ['pin_number', 'pinNumber']) === elementPinNumber);
    if (pinNumberMatches.length > 0) {
      return pinNumberMatches;
    }
  }

  const elementPinName = String(element.getAttribute('data-pin') || '').trim().toLowerCase();
  if (elementPinName) {
    const pinNameMatches = safePins.filter((pin) => getPinValue(pin, ['pin_name', 'pinName']).toLowerCase() === elementPinName);
    if (pinNameMatches.length > 0) {
      return pinNameMatches;
    }
  }

  return [];
};

const resolvePinForElement = (pins, element) => {
  const assignedPinId = getElementAssignedPinId(element);
  if (assignedPinId) {
    const matchedPin = (Array.isArray(pins) ? pins : []).find((pin) => String(pin.id) === assignedPinId);
    if (matchedPin) {
      return matchedPin;
    }
  }

  const candidates = getPinMatchCandidatesForElement(pins, element);
  if (candidates.length === 1) {
    return candidates[0];
  }

  return findPinForElement(pins, element);
};

const getWorkspacePointFromClient = (workspace, clientX, clientY, zoom = 1) => {
  const rect = workspace?.getBoundingClientRect();
  if (!rect) return null;

  const safeZoom = Number(zoom) || 1;

  return {
    x: (clientX - rect.left) / safeZoom,
    y: (clientY - rect.top) / safeZoom,
  };
};

const getPinElementCenter = (workspace, element, zoom = 1) => {
  if (!(element instanceof Element)) {
    return null;
  }

  const workspaceRect = workspace?.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  if (!workspaceRect || elementRect.width <= 0 || elementRect.height <= 0) {
    return null;
  }

  const safeZoom = Number(zoom) || 1;

  return {
    x: (elementRect.left - workspaceRect.left + (elementRect.width / 2)) / safeZoom,
    y: (elementRect.top - workspaceRect.top + (elementRect.height / 2)) / safeZoom,
  };
};

const buildConnectionKey = (connection) => `${String(connection?.sensorPin || '')}:${String(connection?.microcontrollerPin || '')}`;

const getConnectionPath = (startPoint, endPoint) => {
  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;

  if (Math.abs(dx) >= Math.abs(dy)) {
    const bend = Math.min(120, Math.max(40, Math.abs(dx) * 0.35));
    const direction = dx >= 0 ? 1 : -1;
    const control1X = startPoint.x + (bend * direction);
    const control2X = endPoint.x - (bend * direction);
    return `M ${startPoint.x} ${startPoint.y} C ${control1X} ${startPoint.y}, ${control2X} ${endPoint.y}, ${endPoint.x} ${endPoint.y}`;
  }

  const bend = Math.min(120, Math.max(40, Math.abs(dy) * 0.35));
  const direction = dy >= 0 ? 1 : -1;
  const control1Y = startPoint.y + (bend * direction);
  const control2Y = endPoint.y - (bend * direction);
  return `M ${startPoint.x} ${startPoint.y} C ${startPoint.x} ${control1Y}, ${endPoint.x} ${control2Y}, ${endPoint.x} ${endPoint.y}`;
};

const getPinDisplayName = (pin) => String(pin?.pin_name ?? pin?.pinName ?? pin?.pin_number ?? pin?.pinNumber ?? 'Pin');

const rewireConnectionList = (currentConnections, originalConnection, movingSide, nextPinId, nextMarkerKey) => {
  const safeConnections = Array.isArray(currentConnections) ? currentConnections : [];
  const originalKey = buildConnectionKey(originalConnection);
  const updatedConnection = movingSide === 'sensor'
    ? { ...originalConnection, sensorPin: nextPinId, sensorMarkerKey: nextMarkerKey || originalConnection.sensorMarkerKey || '' }
    : { ...originalConnection, microcontrollerPin: nextPinId, microcontrollerMarkerKey: nextMarkerKey || originalConnection.microcontrollerMarkerKey || '' };
  const updatedKey = buildConnectionKey(updatedConnection);
  const nextConnections = [];
  let inserted = false;

  safeConnections.forEach((connection) => {
    const connectionKey = buildConnectionKey(connection);

    if (connectionKey === originalKey) {
      if (!nextConnections.some((existingConnection) => buildConnectionKey(existingConnection) === updatedKey)) {
        nextConnections.push(updatedConnection);
        inserted = true;
      }
      return;
    }

    if (connectionKey === updatedKey) {
      return;
    }

    nextConnections.push(connection);
  });

  if (!inserted && !nextConnections.some((connection) => buildConnectionKey(connection) === updatedKey)) {
    nextConnections.push(updatedConnection);
  }

  return nextConnections.filter((connection) => connection?.sensorPin && connection?.microcontrollerPin);
};

const WiringConnectionEditor = ({
  sensorBoard,
  microcontrollerBoard,
  sensorPins,
  microcontrollerPins,
  connections,
  onConnectionsChange,
  onGeneratedSvgChange,
  onDiagramInteraction,
  initialDiagramSvg = '',
  restoreInitialDiagram = false,
  disabled = false,
  emphasizeSuggestions = false,
}) => {
  const workspaceRef = useRef(null);
  const sensorHostRef = useRef(null);
  const microcontrollerHostRef = useRef(null);
  const interactionRef = useRef(null);
  const boardIdentityRef = useRef('');

  const [workspaceSize, setWorkspaceSize] = useState({
    width: WORKSPACE_DEFAULT_WIDTH,
    height: WORKSPACE_DEFAULT_HEIGHT,
  });
  const [boardLayouts, setBoardLayouts] = useState({ sensor: null, microcontroller: null });
  const [boardRotations, setBoardRotations] = useState({ sensor: 0, microcontroller: 0 });
  const [boardScaleAdjustments, setBoardScaleAdjustments] = useState({ sensor: 1, microcontroller: 1 });
  const [boardOrder, setBoardOrder] = useState(['sensor', 'microcontroller']);
  const [activeBoardSide, setActiveBoardSide] = useState(null);
  const [pinLayout, setPinLayout] = useState({ sensor: {}, microcontroller: {} });
  const [markerLayout, setMarkerLayout] = useState({ sensor: {}, microcontroller: {} });
  const [dragPreview, setDragPreview] = useState(null);
  const [appliedRestoreKey, setAppliedRestoreKey] = useState('');

  const boardIdentity = `${sensorBoard?.id || 'sensor'}:${microcontrollerBoard?.id || 'microcontroller'}`;
  const sensorRenderedSvg = getRenderedSvgContent(sensorBoard);
  const microcontrollerRenderedSvg = getRenderedSvgContent(microcontrollerBoard);
  const sensorSourceSvg = getSourceSvgContent(sensorBoard);
  const microcontrollerSourceSvg = getSourceSvgContent(microcontrollerBoard);

  const wireWidth = 3;

  const tipRadius = Math.max(4, wireWidth * 1.6);
  const tipStroke = Math.max(2, wireWidth * 0.8);
  const hitRadius = Math.max(10, wireWidth * 3); // for dragging

  const getWireColor = useCallback((_connection, index) => {
    return CONNECTION_COLORS[index % CONNECTION_COLORS.length];
  }, []);

  const sensorWorkspaceSvg = useMemo(
    () => stripBoardCalloutsFromSvg(sensorRenderedSvg, sensorSourceSvg),
    [sensorRenderedSvg, sensorSourceSvg]
  );
  const microcontrollerWorkspaceSvg = useMemo(
    () => stripBoardCalloutsFromSvg(microcontrollerRenderedSvg, microcontrollerSourceSvg),
    [microcontrollerRenderedSvg, microcontrollerSourceSvg]
  );

  const sensorMarkup = useMemo(
    () => prepareInlineSvgMarkup(sensorWorkspaceSvg, { extraCssText: BOARD_EDITOR_SVG_CSS }),
    [sensorWorkspaceSvg]
  );
  const microcontrollerMarkup = useMemo(
    () => prepareInlineSvgMarkup(microcontrollerWorkspaceSvg, { extraCssText: BOARD_EDITOR_SVG_CSS }),
    [microcontrollerWorkspaceSvg]
  );

  const boardMetrics = useMemo(() => ({
    sensor: parseSvgMetrics(sensorSourceSvg || sensorWorkspaceSvg),
    microcontroller: parseSvgMetrics(microcontrollerSourceSvg || microcontrollerWorkspaceSvg),
  }), [microcontrollerSourceSvg, microcontrollerWorkspaceSvg, sensorSourceSvg, sensorWorkspaceSvg]);

  const commonScale = useMemo(() => {
    const width = Number(workspaceSize?.width) || WORKSPACE_DEFAULT_WIDTH;
    const height = Number(workspaceSize?.height) || WORKSPACE_DEFAULT_HEIGHT;
    const maxBoardWidth = Math.max(boardMetrics.sensor.width, boardMetrics.microcontroller.width, 1);
    const maxBoardHeight = Math.max(boardMetrics.sensor.height, boardMetrics.microcontroller.height, 1);
    const widthBudget = Math.max(260, width * 0.32);
    const heightBudget = Math.max(220, height * 0.54);
    const scale = Math.min(widthBudget / maxBoardWidth, heightBudget / maxBoardHeight, 3.8);
    return clamp(scale, 0.12, 3.8);
  }, [boardMetrics.microcontroller.height, boardMetrics.microcontroller.width, boardMetrics.sensor.height, boardMetrics.sensor.width, workspaceSize.height, workspaceSize.width]);

    const boardFootprints = useMemo(() => ({
      sensor: getBoardFootprint(
        boardMetrics.sensor,
        boardRotations.sensor,
        commonScale,
        boardScaleAdjustments.sensor,
        'sensor'
      ),
      microcontroller: getBoardFootprint(
        boardMetrics.microcontroller,
        boardRotations.microcontroller,
        commonScale,
        boardScaleAdjustments.microcontroller,
        'microcontroller'
      ),
    }), [
    boardMetrics.microcontroller,
    boardMetrics.sensor,
    boardRotations.microcontroller,
    boardRotations.sensor,
    boardScaleAdjustments.microcontroller,
    boardScaleAdjustments.sensor,
    commonScale
  ]);
  const initialDiagramSnapshot = useMemo(
    () => parseSavedDiagramSnapshot(initialDiagramSvg),
    [initialDiagramSvg]
  );
  const shouldRestoreInitialDiagram = Boolean(restoreInitialDiagram && initialDiagramSnapshot);
  const restoreKey = shouldRestoreInitialDiagram
    ? [
        boardIdentity,
        initialDiagramSnapshot.canvasWidth,
        initialDiagramSnapshot.canvasHeight,
        initialDiagramSnapshot.sensor?.x,
        initialDiagramSnapshot.sensor?.y,
        initialDiagramSnapshot.sensor?.rawWidth,
        initialDiagramSnapshot.sensor?.rawHeight,
        initialDiagramSnapshot.sensor?.rotation,
        initialDiagramSnapshot.sensor?.scaleFactor,
        initialDiagramSnapshot.microcontroller?.x,
        initialDiagramSnapshot.microcontroller?.y,
        initialDiagramSnapshot.microcontroller?.rawWidth,
        initialDiagramSnapshot.microcontroller?.rawHeight,
        initialDiagramSnapshot.microcontroller?.rotation,
        initialDiagramSnapshot.microcontroller?.scaleFactor,
      ].join(':')
    : '';
  const restorePending = shouldRestoreInitialDiagram && appliedRestoreKey !== restoreKey;

  const sensorPinsById = useMemo(
    () => new Map((Array.isArray(sensorPins) ? sensorPins : []).map((pin) => [String(pin.id), pin])),
    [sensorPins]
  );
  const microcontrollerPinsById = useMemo(
    () => new Map((Array.isArray(microcontrollerPins) ? microcontrollerPins : []).map((pin) => [String(pin.id), pin])),
    [microcontrollerPins]
  );

  const normalizedConnections = useMemo(
    () => (Array.isArray(connections) ? connections : []).filter((connection) => connection?.sensorPin && connection?.microcontrollerPin),
    [connections]
  );

  const bringBoardToFront = useCallback((side) => {
    setActiveBoardSide(side);
    setBoardOrder((currentOrder) => {
      const filtered = currentOrder.filter((entry) => entry !== side);
      return [...filtered, side];
    });
  }, []);

  const [canvasZoom, setCanvasZoom] = useState(1);

  const adjustCanvasZoom = useCallback((delta) => {
    setCanvasZoom((current) => clamp(Number((current + delta).toFixed(2)), 0.5, 3));
  }, []);

  const resetCanvasZoom = useCallback(() => {
    setCanvasZoom(1);
  }, []);

  useEffect(() => {
    if (shouldRestoreInitialDiagram) {
      return;
    }

    setBoardRotations({ sensor: 0, microcontroller: 0 });
    setBoardScaleAdjustments({ sensor: 1, microcontroller: 1 });
  }, [boardIdentity, shouldRestoreInitialDiagram]);

  useEffect(() => {
    if (!restorePending) {
      return;
    }

    const workspaceRect = workspaceRef.current?.getBoundingClientRect();
    const effectiveWorkspaceSize = workspaceRect && workspaceRect.width > 0 && workspaceRect.height > 0
      ? {
          width: workspaceRect.width,
          height: workspaceRect.height,
        }
      : workspaceSize;

    if (
      effectiveWorkspaceSize.width !== workspaceSize.width
      || effectiveWorkspaceSize.height !== workspaceSize.height
    ) {
      setWorkspaceSize(effectiveWorkspaceSize);
    }

    const restoredState = buildRestoredBoardState(
      initialDiagramSnapshot,
      boardMetrics,
      commonScale,
      effectiveWorkspaceSize
    );
    if (!restoredState) {
      setAppliedRestoreKey(restoreKey);
      return;
    }

    boardIdentityRef.current = boardIdentity;
    setBoardOrder(['sensor', 'microcontroller']);
    setActiveBoardSide(null);
    setBoardRotations(restoredState.rotations);
    setBoardScaleAdjustments(restoredState.scaleAdjustments);
    setBoardLayouts(restoredState.layouts);
    setAppliedRestoreKey(restoreKey);
  }, [
    boardIdentity,
    boardMetrics,
    commonScale,
    initialDiagramSnapshot,
    restoreKey,
    restorePending,
    workspaceSize,
  ]);

  useEffect(() => {
    const updateWorkspaceSize = () => {
      const rect = workspaceRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) {
        return;
      }

      setWorkspaceSize({
        width: rect.width,
        height: rect.height,
      });
    };

    updateWorkspaceSize();
    window.addEventListener('resize', updateWorkspaceSize);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && workspaceRef.current) {
      resizeObserver = new ResizeObserver(() => updateWorkspaceSize());
      resizeObserver.observe(workspaceRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateWorkspaceSize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const buildInitialBoardLayout = useCallback(() => {
    const width = Number(workspaceSize?.width) || WORKSPACE_DEFAULT_WIDTH;
    const height = Number(workspaceSize?.height) || WORKSPACE_DEFAULT_HEIGHT;
    const sensorFootprint = boardFootprints.sensor;
    const microcontrollerFootprint = boardFootprints.microcontroller;

    if (width >= 920) {
      return {
        sensor: clampBoardPosition({
          x: (width * 0.26) - (sensorFootprint.renderWidth / 2),
          y: (height - sensorFootprint.renderHeight) / 2,
        }, sensorFootprint, workspaceSize),
        microcontroller: clampBoardPosition({
          x: (width * 0.74) - (microcontrollerFootprint.renderWidth / 2),
          y: (height - microcontrollerFootprint.renderHeight) / 2,
        }, microcontrollerFootprint, workspaceSize),
      };
    }

    return {
      sensor: clampBoardPosition({
        x: (width - sensorFootprint.renderWidth) / 2,
        y: 48,
      }, sensorFootprint, workspaceSize),
      microcontroller: clampBoardPosition({
        x: (width - microcontrollerFootprint.renderWidth) / 2,
        y: 48 + sensorFootprint.renderHeight + 88,
      }, microcontrollerFootprint, workspaceSize),
    };
  }, [boardFootprints.microcontroller, boardFootprints.sensor, workspaceSize]);

  const resetBoardLayout = useCallback(() => {
    if (typeof onDiagramInteraction === 'function') {
      onDiagramInteraction();
    }

    boardIdentityRef.current = boardIdentity;
    setBoardLayouts(buildInitialBoardLayout());
    setBoardOrder(['sensor', 'microcontroller']);
    setActiveBoardSide(null);
  }, [boardIdentity, buildInitialBoardLayout, onDiagramInteraction]);

  useEffect(() => {
    if (restorePending) {
      return;
    }

    setBoardLayouts((currentLayouts) => {
      if (boardIdentityRef.current !== boardIdentity || !currentLayouts.sensor || !currentLayouts.microcontroller) {
        boardIdentityRef.current = boardIdentity;
        return buildInitialBoardLayout();
      }

      const nextSensor = clampBoardPosition(currentLayouts.sensor, boardFootprints.sensor, workspaceSize);
      const nextMicrocontroller = clampBoardPosition(currentLayouts.microcontroller, boardFootprints.microcontroller, workspaceSize);

      if (
        positionsMatch(nextSensor, currentLayouts.sensor)
        && positionsMatch(nextMicrocontroller, currentLayouts.microcontroller)
      ) {
        return currentLayouts;
      }

      return {
        sensor: nextSensor,
        microcontroller: nextMicrocontroller,
      };
    });
  }, [boardFootprints.microcontroller, boardFootprints.sensor, boardIdentity, buildInitialBoardLayout, restorePending, workspaceSize]);

  const collectPinLayout = useCallback(() => {
    const workspace = workspaceRef.current;
    if (!workspace) {
      return;
    }

    const buildLayoutForSide = (pins, host, side) => {
      const nextPinLayout = {};
      const nextMarkerLayout = {};
      if (!host) {
        return { pinLayout: nextPinLayout, markerLayout: nextMarkerLayout };
      }

      const interactiveElements = Array.from(host.querySelectorAll('.pin-hole, .pin-rect'))
        .filter((element) => element.getAttribute('data-micropinouts-callout') !== '1');

      interactiveElements.forEach((element, index) => {
        const markerKey = `${side}:${index}`;
        element.setAttribute('data-wire-marker-key', markerKey);
        element.removeAttribute('data-wire-pin-id');

        const matchedPin = resolvePinForElement(pins, element);
        const center = getPinElementCenter(workspace, element, canvasZoom);
        if (matchedPin) {
          element.setAttribute('data-wire-pin-id', String(matchedPin.id));
        }

        if (center) {
          nextMarkerLayout[markerKey] = center;
          if (matchedPin && !nextPinLayout[String(matchedPin.id)]) {
            nextPinLayout[String(matchedPin.id)] = center;
          }
        }
      });

      return { pinLayout: nextPinLayout, markerLayout: nextMarkerLayout };
    };

    const sensorLayout = buildLayoutForSide(sensorPins, sensorHostRef.current, 'sensor');
    const microcontrollerLayout = buildLayoutForSide(microcontrollerPins, microcontrollerHostRef.current, 'microcontroller');

    setPinLayout({
      sensor: sensorLayout.pinLayout,
      microcontroller: microcontrollerLayout.pinLayout,
    });
    setMarkerLayout({
      sensor: sensorLayout.markerLayout,
      microcontroller: microcontrollerLayout.markerLayout,
    });
  }, [microcontrollerPins, sensorPins, canvasZoom]);

  useEffect(() => {
    let timeoutId = null;
    let resizeObserver = null;
    const frameId = window.requestAnimationFrame(collectPinLayout);

    timeoutId = window.setTimeout(collectPinLayout, 180);
    window.addEventListener('resize', collectPinLayout);

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => collectPinLayout());
      if (workspaceRef.current) {
        resizeObserver.observe(workspaceRef.current);
      }
      if (sensorHostRef.current) {
        resizeObserver.observe(sensorHostRef.current);
      }
      if (microcontrollerHostRef.current) {
        resizeObserver.observe(microcontrollerHostRef.current);
      }
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', collectPinLayout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [boardLayouts, boardRotations, boardScaleAdjustments, collectPinLayout, microcontrollerMarkup, sensorMarkup, canvasZoom]);

  const getHoverTargetFromEvent = useCallback((event, targetSide) => {
    if (typeof document === 'undefined') {
      return null;
    }

    const host = targetSide === 'sensor' ? sensorHostRef.current : microcontrollerHostRef.current;
    const pins = targetSide === 'sensor' ? sensorPins : microcontrollerPins;
    if (!host) {
      return null;
    }

    const stackedElements = document.elementsFromPoint(event.clientX, event.clientY);
    const targetElement = stackedElements.find((candidate) => {
      if (!(candidate instanceof Element)) {
        return false;
      }

      if (!host.contains(candidate)) {
        return false;
      }

      if (!candidate.matches('.pin-hole, .pin-rect')) {
        return false;
      }

      return candidate.getAttribute('data-micropinouts-callout') !== '1';
    });

    if (!targetElement) {
      return null;
    }

    const pin = resolvePinForElement(pins, targetElement);
    const point = getPinElementCenter(workspaceRef.current, targetElement, canvasZoom);
    if (!pin || !point) {
      return null;
    }

    return {
      side: targetSide,
      pinId: String(pin.id),
      pin,
      point,
      markerKey: getElementMarkerKey(targetElement),
    };
  }, [microcontrollerPins, sensorPins, canvasZoom]);

  const startConnectionFromPin = useCallback((side, pinElement, event) => {
    const pins = side === 'sensor' ? sensorPins : microcontrollerPins;
    const pin = resolvePinForElement(pins, pinElement);
    const fixedPoint = getPinElementCenter(workspaceRef.current, pinElement, canvasZoom);
    if (!pin || !fixedPoint) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    bringBoardToFront(side);

    const nextPreview = {
      mode: 'create',
      fixedSide: side,
      movingSide: side === 'sensor' ? 'microcontroller' : 'sensor',
      fixedPoint,
      pointer: fixedPoint,
      sourcePinId: String(pin.id),
      sourceMarkerKey: getElementMarkerKey(pinElement),
      hoverTarget: null,
    };

    interactionRef.current = nextPreview;
    setDragPreview(nextPreview);
  }, [bringBoardToFront, microcontrollerPins, sensorPins, canvasZoom]);

  const handleBoardPointerDown = useCallback((side, event) => {
    if (disabled) {
      return;
    }

    const targetElement = event.target instanceof Element ? event.target : null;
    if (!targetElement) {
      return;
    }

    const pinElement = targetElement.closest('.pin-hole, .pin-rect');
    if (pinElement && pinElement.getAttribute('data-micropinouts-callout') !== '1') {
      startConnectionFromPin(side, pinElement, event);
      return;
    }

    const workspacePoint = getWorkspacePointFromClient(workspaceRef.current, event.clientX, event.clientY, canvasZoom);
    const layout = boardLayouts[side];
    if (!workspacePoint || !layout) {
      return;
    }

    event.preventDefault();
    bringBoardToFront(side);

    interactionRef.current = {
      mode: 'board',
      side,
      pointerOffset: {
        x: workspacePoint.x - layout.x,
        y: workspacePoint.y - layout.y,
      },
    };
  }, [boardLayouts, bringBoardToFront, disabled, startConnectionFromPin, canvasZoom]);

  const handleConnectionEndpointPointerDown = useCallback((movingSide, connection, event) => {
    if (disabled) {
      return;
    }

    const fixedSide = movingSide === 'sensor' ? 'microcontroller' : 'sensor';
    const fixedMarkerKey = fixedSide === 'sensor'
      ? String(connection.sensorMarkerKey || '')
      : String(connection.microcontrollerMarkerKey || '');
    const movingMarkerKey = movingSide === 'sensor'
      ? String(connection.sensorMarkerKey || '')
      : String(connection.microcontrollerMarkerKey || '');
    const fixedPoint = fixedSide === 'sensor'
      ? (fixedMarkerKey ? markerLayout.sensor[fixedMarkerKey] : null) || pinLayout.sensor[String(connection.sensorPin)]
      : (fixedMarkerKey ? markerLayout.microcontroller[fixedMarkerKey] : null) || pinLayout.microcontroller[String(connection.microcontrollerPin)];
    const movingPoint = movingSide === 'sensor'
      ? (movingMarkerKey ? markerLayout.sensor[movingMarkerKey] : null) || pinLayout.sensor[String(connection.sensorPin)]
      : (movingMarkerKey ? markerLayout.microcontroller[movingMarkerKey] : null) || pinLayout.microcontroller[String(connection.microcontrollerPin)];

    if (!fixedPoint || !movingPoint) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    bringBoardToFront(movingSide);

    const nextPreview = {
      mode: 'rewire',
      movingSide,
      fixedSide,
      fixedPoint,
      pointer: movingPoint,
      hoverTarget: null,
      originalConnection: connection,
    };

    interactionRef.current = nextPreview;
    setDragPreview(nextPreview);
  }, [bringBoardToFront, disabled, markerLayout.microcontroller, markerLayout.sensor, pinLayout.microcontroller, pinLayout.sensor]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const interaction = interactionRef.current;
      if (!interaction) {
        return;
      }

      if (interaction.mode === 'board') {
        const workspacePoint = getWorkspacePointFromClient(workspaceRef.current, event.clientX, event.clientY, canvasZoom);
        const footprint = boardFootprints[interaction.side];
        if (!workspacePoint || !footprint) {
          return;
        }

        const nextPosition = clampBoardPosition({
          x: workspacePoint.x - interaction.pointerOffset.x,
          y: workspacePoint.y - interaction.pointerOffset.y,
        }, footprint, workspaceSize);

        setBoardLayouts((currentLayouts) => {
          const currentLayout = currentLayouts[interaction.side];
          if (currentLayout && positionsMatch(currentLayout, nextPosition)) {
            return currentLayouts;
          }

          if (typeof onDiagramInteraction === 'function') {
            onDiagramInteraction();
          }

          return {
            ...currentLayouts,
            [interaction.side]: nextPosition,
          };
        });
        return;
      }

      if (interaction.mode === 'create' || interaction.mode === 'rewire') {
        const pointer = getWorkspacePointFromClient(workspaceRef.current, event.clientX, event.clientY, canvasZoom);
        if (!pointer) {
          return;
        }

        const hoverTarget = getHoverTargetFromEvent(event, interaction.movingSide);
        const nextPreview = {
          ...interaction,
          pointer,
          hoverTarget,
        };

        interactionRef.current = nextPreview;
        setDragPreview(nextPreview);
      }
    };

    const handlePointerUp = () => {
      const interaction = interactionRef.current;
      if (!interaction) {
        return;
      }

      if (interaction.mode === 'create' && interaction.hoverTarget) {
        const nextConnection = interaction.fixedSide === 'sensor'
          ? {
            sensorPin: interaction.sourcePinId,
            sensorMarkerKey: interaction.sourceMarkerKey || '',
            microcontrollerPin: interaction.hoverTarget.pinId,
            microcontrollerMarkerKey: interaction.hoverTarget.markerKey || '',
          }
          : {
            sensorPin: interaction.hoverTarget.pinId,
            sensorMarkerKey: interaction.hoverTarget.markerKey || '',
            microcontrollerPin: interaction.sourcePinId,
            microcontrollerMarkerKey: interaction.sourceMarkerKey || '',
          };

        if (typeof onDiagramInteraction === 'function') {
          onDiagramInteraction();
        }

        onConnectionsChange((currentConnections) => {
          const existingConnections = Array.isArray(currentConnections) ? currentConnections : [];
          if (existingConnections.some((connection) => buildConnectionKey(connection) === buildConnectionKey(nextConnection))) {
            return existingConnections;
          }

          return [...existingConnections, nextConnection];
        });
      }

      if (interaction.mode === 'rewire' && interaction.hoverTarget && interaction.originalConnection) {
        if (typeof onDiagramInteraction === 'function') {
          onDiagramInteraction();
        }

        onConnectionsChange((currentConnections) => rewireConnectionList(
          currentConnections,
          interaction.originalConnection,
          interaction.movingSide,
          interaction.hoverTarget.pinId,
          interaction.hoverTarget.markerKey
        ));
      }

      interactionRef.current = null;
      setDragPreview(null);
      setActiveBoardSide(null);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
  }, [boardFootprints, getHoverTargetFromEvent, onConnectionsChange, onDiagramInteraction, workspaceSize, canvasZoom]);

  const rotateBoard = useCallback((side) => {
    if (disabled) {
      return;
    }

    if (typeof onDiagramInteraction === 'function') {
      onDiagramInteraction();
    }

    bringBoardToFront(side);
    setBoardRotations((currentRotations) => ({
      ...currentRotations,
      [side]: normalizeRotation(currentRotations[side] + 90),
    }));
  }, [bringBoardToFront, disabled, onDiagramInteraction]);

  const adjustBoardScale = useCallback((side, delta) => {
    if (disabled) {
      return;
    }

    if (typeof onDiagramInteraction === 'function') {
      onDiagramInteraction();
    }

    bringBoardToFront(side);
    setBoardScaleAdjustments((currentScales) => ({
      ...currentScales,
      [side]: clamp(Number((currentScales[side] + delta).toFixed(2)), 0.45, 6.0),
    }));
  }, [bringBoardToFront, disabled, onDiagramInteraction]);

  const resetBoardScale = useCallback((side) => {
    if (disabled) {
      return;
    }

    if (typeof onDiagramInteraction === 'function') {
      onDiagramInteraction();
    }

    setBoardScaleAdjustments((currentScales) => ({
      ...currentScales,
      [side]: 1,
    }));
  }, [disabled, onDiagramInteraction]);

  const removeConnection = useCallback((connectionToRemove) => {
    if (typeof onDiagramInteraction === 'function') {
      onDiagramInteraction();
    }

    onConnectionsChange((currentConnections) => (
      (Array.isArray(currentConnections) ? currentConnections : []).filter(
        (connection) => buildConnectionKey(connection) !== buildConnectionKey(connectionToRemove)
      )
    ));
  }, [onConnectionsChange, onDiagramInteraction]);

  // Auto Wiring Functionality
  const [autoWiringSuggestions, setAutoWiringSuggestions] = useState([]);
  const [showAutoWiringSuggestions, setShowAutoWiringSuggestions] = useState(false);

  const generateAutoWiring = useCallback(() => {
    if (!sensorPins || !microcontrollerPins) return;

    const suggestions = generateAutoWiringSuggestions(sensorPins, microcontrollerPins);
    setAutoWiringSuggestions(suggestions);
    setShowAutoWiringSuggestions(true);
  }, [sensorPins, microcontrollerPins]);

  const applyAutoWiringSuggestions = useCallback(() => {
    if (autoWiringSuggestions.length === 0) return;

    if (typeof onDiagramInteraction === 'function') {
      onDiagramInteraction();
    }

    const newConnections = autoWiringSuggestions.map(suggestion => ({
      sensorPin: suggestion.sensorPin,
      microcontrollerPin: suggestion.microcontrollerPin,
      sensorMarkerKey: '',
      microcontrollerMarkerKey: ''
    }));

    onConnectionsChange((currentConnections) => {
      const existingConnections = Array.isArray(currentConnections) ? currentConnections : [];
      const existingKeys = new Set(existingConnections.map(conn => buildConnectionKey(conn)));

      // Only add connections that don't already exist
      const connectionsToAdd = newConnections.filter(conn =>
        !existingKeys.has(buildConnectionKey(conn))
      );

      return [...existingConnections, ...connectionsToAdd];
    });

    setShowAutoWiringSuggestions(false);
  }, [autoWiringSuggestions, onConnectionsChange, onDiagramInteraction]);

  const clearAutoWiringSuggestions = useCallback(() => {
    setAutoWiringSuggestions([]);
    setShowAutoWiringSuggestions(false);
  }, []);

  const renderedConnections = useMemo(() => normalizedConnections.map((connection, index) => {
    const sensorPinId = String(connection.sensorPin);
    const microcontrollerPinId = String(connection.microcontrollerPin);
    const sensorPoint = (connection.sensorMarkerKey ? markerLayout.sensor[String(connection.sensorMarkerKey)] : null) || pinLayout.sensor[sensorPinId];
    const microcontrollerPoint = (connection.microcontrollerMarkerKey ? markerLayout.microcontroller[String(connection.microcontrollerMarkerKey)] : null) || pinLayout.microcontroller[microcontrollerPinId];
    const sensorPin = sensorPinsById.get(sensorPinId) || null;
    const microcontrollerPin = microcontrollerPinsById.get(microcontrollerPinId) || null;
    const confidence = getConnectionConfidence(sensorPin, microcontrollerPin);

    return {
      key: buildConnectionKey(connection),
      color: getWireColor(connection, index),
      connection,
      sensorPin,
      microcontrollerPin,
      sensorPoint,
      microcontrollerPoint,
      confidence,
    };
  }), [markerLayout.microcontroller, markerLayout.sensor, microcontrollerPinsById, normalizedConnections, pinLayout.microcontroller, pinLayout.sensor, sensorPinsById, getWireColor]);

  const manualPreviewSvg = useMemo(() => {
    if (!sensorBoard || !microcontrollerBoard || normalizedConnections.length === 0) {
      return '';
    }

    const sensorLayout = boardLayouts.sensor;
    const microcontrollerLayout = boardLayouts.microcontroller;
    const sensorFootprint = boardFootprints.sensor;
    const microcontrollerFootprint = boardFootprints.microcontroller;
    if (!sensorLayout || !microcontrollerLayout || !sensorFootprint || !microcontrollerFootprint) {
      return '';
    }

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    const includeRect = (x, y, width, height) => {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + width);
      maxY = Math.max(maxY, y + height);
    };

    const includePoint = (point) => {
      if (!point) {
        return;
      }

      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    };

    includeRect(sensorLayout.x, sensorLayout.y, sensorFootprint.renderWidth, sensorFootprint.renderHeight);
    includeRect(microcontrollerLayout.x, microcontrollerLayout.y, microcontrollerFootprint.renderWidth, microcontrollerFootprint.renderHeight);
    renderedConnections.forEach(({ sensorPoint, microcontrollerPoint }) => {
      includePoint(sensorPoint);
      includePoint(microcontrollerPoint);
    });

    if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
      return '';
    }

    const padding = 28;
    const contentWidth = Math.max(1, maxX - minX);
    const contentHeight = Math.max(1, maxY - minY);
    const paddedWidth = Math.ceil(contentWidth + (padding * 2));
    const paddedHeight = Math.ceil(contentHeight + (padding * 2));
    const exportWidth = Math.max(480, paddedWidth);
    const exportHeight = Math.max(340, paddedHeight);
    const extraOffsetX = (exportWidth - paddedWidth) / 2;
    const extraOffsetY = (exportHeight - paddedHeight) / 2;
    const translateX = padding - minX + extraOffsetX;
    const translateY = padding - minY + extraOffsetY;

    const buildBoardMarkup = (workspaceSvg, footprint, layout, rotation) => {
      const x = layout.x + translateX + footprint.innerOffsetX;
      const y = layout.y + translateY + footprint.innerOffsetY;
      const centerX = layout.x + translateX + (footprint.renderWidth / 2);
      const centerY = layout.y + translateY + (footprint.renderHeight / 2);
      const embedded = prepareEmbeddedSvgMarkup(workspaceSvg, {
        x,
        y,
        width: footprint.baseWidth,
        height: footprint.baseHeight,
        extraCssText: BOARD_EXPORT_SVG_CSS,
      });

      if (normalizeRotation(rotation) === 0) {
        return embedded;
      }

      return `<g transform="rotate(${normalizeRotation(rotation)} ${centerX} ${centerY})">${embedded}</g>`;
    };

    const connectionMarkup = renderedConnections.map(({ color, sensorPoint, microcontrollerPoint, connection }, index) => {
      if (!sensorPoint || !microcontrollerPoint) {
        return '';
      }

      const translatedSensorPoint = {
        x: sensorPoint.x + translateX,
        y: sensorPoint.y + translateY,
      };
      const translatedMicrocontrollerPoint = {
        x: microcontrollerPoint.x + translateX,
        y: microcontrollerPoint.y + translateY,
      };

      const connectionKey = buildConnectionKey(connection);

      return `
        <g
          class="wiring-guide-connection"
          data-connection-index="${index}"
          data-connection-key="${connectionKey}"
          data-sensor-pin-id="${connection.sensorPin}"
          data-microcontroller-pin-id="${connection.microcontrollerPin}"
        >
          <path
            class="wiring-guide-wire"
            data-connection-index="${index}"
            data-connection-key="${connectionKey}"
            d="${getConnectionPath(translatedSensorPoint, translatedMicrocontrollerPoint)}"
            fill="none"
            stroke="${color}"
            stroke-width="${tipStroke}"
            stroke-linecap="round"
            stroke-linejoin="round"
            opacity="0.92"
          />
          <circle
            class="wiring-guide-wire-endpoint"
            data-connection-index="${index}"
            cx="${translatedSensorPoint.x}"
            cy="${translatedSensorPoint.y}"
            r="${tipRadius}"
            fill="#ffffff"
            stroke="${color}"
            stroke-width="${tipStroke}"
          />
          <circle
            class="wiring-guide-wire-endpoint"
            data-connection-index="${index}"
            cx="${translatedMicrocontrollerPoint.x}"
            cy="${translatedMicrocontrollerPoint.y}"
            r="${tipRadius}"
            fill="#ffffff"
            stroke="${color}"
            stroke-width="${tipStroke}"
          />
        </g>
      `;
    }).join('');

    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="${exportWidth}" height="${exportHeight}" viewBox="0 0 ${exportWidth} ${exportHeight}">
        <rect width="${exportWidth}" height="${exportHeight}" fill="${WIRING_GUIDE_CANVAS_FILL}" />
        ${buildBoardMarkup(sensorWorkspaceSvg, sensorFootprint, sensorLayout, boardRotations.sensor)}
        ${buildBoardMarkup(microcontrollerWorkspaceSvg, microcontrollerFootprint, microcontrollerLayout, boardRotations.microcontroller)}
        ${connectionMarkup}
      </svg>
    `.trim();
  }, [
    boardFootprints.microcontroller,
    boardFootprints.sensor,
    boardLayouts.microcontroller,
    boardLayouts.sensor,
    boardRotations.microcontroller,
    boardRotations.sensor,
    microcontrollerBoard,
    normalizedConnections.length,
    renderedConnections,
    sensorBoard,
    sensorWorkspaceSvg,
    microcontrollerWorkspaceSvg,
    tipRadius,
    tipStroke
  ]);

  useEffect(() => {
    if (typeof onGeneratedSvgChange === 'function') {
      onGeneratedSvgChange(manualPreviewSvg);
    }
  }, [manualPreviewSvg, onGeneratedSvgChange]);

  const draftStartPoint = dragPreview?.fixedPoint || null;
  const draftEndPoint = dragPreview?.hoverTarget?.point || dragPreview?.pointer || null;

  const renderBoard = (side) => {
    const board = side === 'sensor' ? sensorBoard : microcontrollerBoard;
    const markup = side === 'sensor' ? sensorMarkup : microcontrollerMarkup;
    const footprint = boardFootprints[side];
    const layout = boardLayouts[side];
    const rotation = boardRotations[side];
    const scale = boardScaleAdjustments[side];
    const hostRef = side === 'sensor' ? sensorHostRef : microcontrollerHostRef;
    const zIndex = boardOrder.indexOf(side) + 1;

    if (!footprint || !layout) {
      return null;
    }

    const panelLeft = clamp(
      layout.x + (footprint.renderWidth / 2) - (BOARD_PANEL_WIDTH / 2),
      WORKSPACE_EDGE_PADDING,
      Math.max(WORKSPACE_EDGE_PADDING, workspaceSize.width - BOARD_PANEL_WIDTH - WORKSPACE_EDGE_PADDING)
    );
    const preferredPanelTop = layout.y - BOARD_PANEL_HEIGHT - BOARD_PANEL_GAP;
    const fallbackPanelTop = layout.y + footprint.renderHeight + BOARD_PANEL_GAP;
    const maxPanelTop = Math.max(
      WORKSPACE_EDGE_PADDING,
      workspaceSize.height - BOARD_PANEL_HEIGHT - WORKSPACE_EDGE_PADDING
    );
    const panelTop = preferredPanelTop >= WORKSPACE_EDGE_PADDING
      ? preferredPanelTop
      : Math.min(maxPanelTop, fallbackPanelTop);

    return (
      <React.Fragment key={side}>
        <BoardSprite
          $active={activeBoardSide === side}
          $dragging={interactionRef.current?.mode === 'board' && interactionRef.current?.side === side}
          style={{
            width: `${footprint.renderWidth}px`,
            height: `${footprint.renderHeight}px`,
            transform: `translate3d(${layout.x}px, ${layout.y}px, 0)`,
            zIndex,
          }}
          onPointerDown={(event) => handleBoardPointerDown(side, event)}
        >
          {markup ? (
            <RotatedBoardFrame
              style={{
                left: `${footprint.innerOffsetX}px`,
                top: `${footprint.innerOffsetY}px`,
                width: `${footprint.baseWidth}px`,
                height: `${footprint.baseHeight}px`,
                transform: `rotate(${rotation}deg)`,
              }}
            >
              <BoardSvgHost ref={hostRef} dangerouslySetInnerHTML={{ __html: markup }} />
            </RotatedBoardFrame>
          ) : (
            <BoardFallback>
              {board?.name || 'Board'}
            </BoardFallback>
          )}
        </BoardSprite>

        <BoardFloatingPanel
          style={{
            left: `${panelLeft}px`,
            top: `${panelTop}px`,
            width: `${BOARD_PANEL_WIDTH}px`,
            zIndex: zIndex + 20,
          }}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <BoardFloatingName title={board?.name || side}>
            {board?.name || 'Board'}
          </BoardFloatingName>

          <BoardFloatingActions>
            <FloatingControlButton type="button" onClick={() => adjustBoardScale(side, -0.1)} disabled={disabled} title={`Make ${board?.name || side} smaller`}>
              <Minus size={14} />
            </FloatingControlButton>
            <FloatingScaleChip onDoubleClick={() => resetBoardScale(side)} title="Double-click to reset board size">
              {Math.round(scale * 100)}%
            </FloatingScaleChip>
            <FloatingControlButton type="button" onClick={() => adjustBoardScale(side, 0.1)} disabled={disabled} title={`Make ${board?.name || side} larger`}>
              <Plus size={14} />
            </FloatingControlButton>
            <FloatingControlButton type="button" onClick={() => rotateBoard(side)} disabled={disabled} title={`Rotate ${board?.name || side} 90 degrees`}>
              <RotateCw size={16} />
            </FloatingControlButton>
          </BoardFloatingActions>
        </BoardFloatingPanel>
      </React.Fragment>
    );
  };

  return (
    <EditorShell>
      <InstructionsCard>
        <InstructionsTop>
          <InstructionsTitle>
            <Link2 size={16} />
            Single-Canvas Wiring Editor
          </InstructionsTitle>

          <ResetButton type="button" onClick={resetBoardLayout} disabled={disabled}>
            <RefreshCw size={14} />
            Reset Board Positions
          </ResetButton>
        </InstructionsTop>

        <InstructionsText>
          Drag boards directly on the shared canvas, use each board&apos;s floating control panel to resize or rotate it, then drag wires between pinholes.
          Existing wire endpoints are also draggable so you can reassign a connection without deleting it.
        </InstructionsText>
      </InstructionsCard>

      <BoardControlCard $emphasized={emphasizeSuggestions}>
        <BoardControlMeta>
          <BoardControlLabel>Suggest Wiring</BoardControlLabel>
          <BoardControlTitle>Smart Connection Suggestions</BoardControlTitle>
          <BoardControlHint>Suggest wiring based on pin compatibility</BoardControlHint>
        </BoardControlMeta>

        <BoardControlActions>
          {emphasizeSuggestions ? (
            <SuggestionPrimaryButton
              type="button"
              onClick={generateAutoWiring}
              disabled={disabled || !sensorBoard || !microcontrollerBoard}
              $active={showAutoWiringSuggestions && autoWiringSuggestions.length > 0}
              title="Suggest wiring connections"
            >
              <Link2 size={15} />
              {showAutoWiringSuggestions && autoWiringSuggestions.length > 0 ? 'Refresh Suggestions' : 'Suggest Wiring'}
            </SuggestionPrimaryButton>
          ) : (
          <ControlButton
            type="button"
            onClick={generateAutoWiring}
            disabled={disabled || !sensorBoard || !microcontrollerBoard}
            title="Suggest wiring connections"
          >
            <Link2 size={15} />
          </ControlButton>
          )}

          {showAutoWiringSuggestions && autoWiringSuggestions.length > 0 && (
            <>
              <SuggestionApplyButton
                type="button"
                onClick={applyAutoWiringSuggestions}
                disabled={disabled}
                title="Apply suggestions"
              >
                <Check size={16} />
              </SuggestionApplyButton>

              <ControlButton
                type="button"
                onClick={clearAutoWiringSuggestions}
                disabled={disabled}
                title="Clear suggestions"
                style={{ background: '#ef4444', borderColor: '#ef4444', color: 'white' }}
              >
                X
              </ControlButton>
            </>
          )}

          {showAutoWiringSuggestions && (
            <ScaleChip>
              {autoWiringSuggestions.length} suggestion{autoWiringSuggestions.length !== 1 ? 's' : ''}
            </ScaleChip>
          )}
        </BoardControlActions>
      </BoardControlCard>



      {/*
            <BoardControlMeta>
              <BoardControlLabel>{item.label}</BoardControlLabel>
              <BoardControlTitle>{item.board?.name || 'Select a board'}</BoardControlTitle>
              <BoardControlHint>{item.hint} • Rotation {item.rotation}&deg;</BoardControlHint>
            </BoardControlMeta>

            <BoardControlActions>
              <ControlButton type="button" onClick={() => adjustBoardScale(item.side, -0.1)} disabled={disabled} title="Make smaller">
                <Minus size={15} />
              </ControlButton>
              <ScaleChip onDoubleClick={() => resetBoardScale(item.side)} title="Double-click to reset size">
                {Math.round(item.scale * 100)}%
              </ScaleChip>
              <ControlButton type="button" onClick={() => adjustBoardScale(item.side, 0.1)} disabled={disabled} title="Make larger">
                <Plus size={15} />
              </ControlButton>
            </BoardControlActions>
          </BoardControlCard>
        ))}
      </BoardControls>

      <BoardControlCard>
        <BoardControlMeta>
          <BoardControlLabel>Canvas View</BoardControlLabel>
          <BoardControlTitle>Workspace Zoom</BoardControlTitle>
          <BoardControlHint>View only • Does not affect exported SVG</BoardControlHint>
        </BoardControlMeta>

        <BoardControlActions>
          <ControlButton type="button" onClick={() => adjustCanvasZoom(-0.1)} disabled={disabled} title="Zoom out canvas">
            <Minus size={15} />
          </ControlButton>

          <ScaleChip onDoubleClick={resetCanvasZoom} title="Double-click to reset canvas zoom">
            {Math.round(canvasZoom * 100)}%
          </ScaleChip>

          <ControlButton type="button" onClick={() => adjustCanvasZoom(0.1)} disabled={disabled} title="Zoom in canvas">
            <Plus size={15} />
          </ControlButton>
        </BoardControlActions>
      </BoardControlCard>
      <BoardControlCard>
        <BoardControlMeta>
          <BoardControlLabel>Suggest Wiring</BoardControlLabel>
          <BoardControlTitle>Smart Connection Suggestions</BoardControlTitle>
          <BoardControlHint>Suggest wiring based on pin compatibility</BoardControlHint>
        </BoardControlMeta>

        <BoardControlActions>
          <ControlButton
            type="button"
            onClick={generateAutoWiring}
            disabled={disabled || !sensorBoard || !microcontrollerBoard}
            title="Suggest wiring connections"
          >
            <Link2 size={15} />
          </ControlButton>

          {showAutoWiringSuggestions && autoWiringSuggestions.length > 0 && (
            <>
              <SuggestionApplyButton
                type="button"
                onClick={applyAutoWiringSuggestions}
                disabled={disabled}
                title="Apply suggestions"
              >
                <Check size={16} />
              </SuggestionApplyButton>

              <ControlButton
                type="button"
                onClick={clearAutoWiringSuggestions}
                disabled={disabled}
                title="Clear suggestions"
                style={{ background: '#ef4444', borderColor: '#ef4444', color: 'white' }}
              >
                X
              </ControlButton>
            </>
          )}

          {showAutoWiringSuggestions && (
            <ScaleChip>
              {autoWiringSuggestions.length} suggestion{autoWiringSuggestions.length !== 1 ? 's' : ''}
            </ScaleChip>
          )}
        </BoardControlActions>
      </BoardControlCard>

      */}

      <Workspace ref={workspaceRef}>
        {restorePending && (
          <div
            style={{
              position: 'absolute',
              inset: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#475569',
              fontSize: '14px',
              fontWeight: 600,
              zIndex: 2,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,251,255,0.96) 100%)',
            }}
          >
            Restoring the saved diagram layout...
          </div>
        )}
        <WorkspaceToolbar>
          <FloatingControlButton type="button" onClick={() => adjustCanvasZoom(-0.1)} disabled={disabled} title="Zoom out canvas">
            <Minus size={14} />
          </FloatingControlButton>
          <FloatingScaleChip onDoubleClick={resetCanvasZoom} title="Double-click to reset canvas zoom">
            {Math.round(canvasZoom * 100)}%
          </FloatingScaleChip>
          <FloatingControlButton type="button" onClick={() => adjustCanvasZoom(0.1)} disabled={disabled} title="Zoom in canvas">
            <Plus size={14} />
          </FloatingControlButton>
        </WorkspaceToolbar>
        <WorkspaceZoomLayer
          style={{
            width: `${workspaceSize.width}px`,
            height: `${workspaceSize.height}px`,
            transform: `scale(${canvasZoom})`,
          }}
        >
        {renderBoard('sensor')}
        {renderBoard('microcontroller')}

        <Overlay aria-hidden="true">
          {renderedConnections.map(({ key, color, connection, sensorPoint, microcontrollerPoint }) => {
            if (!sensorPoint || !microcontrollerPoint) {
              return null;
            }

            return (
              <g key={key}>
                <path
                  d={getConnectionPath(sensorPoint, microcontrollerPoint)}
                  fill="none"
                  stroke={color}
                  strokeWidth={wireWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.9"
                />
                <circle
                    cx={sensorPoint.x}
                    cy={sensorPoint.y}
                    r={tipRadius}
                    fill="#ffffff"
                    stroke={color}
                    strokeWidth={tipStroke}
                  />
                <circle 
                    cx={microcontrollerPoint.x} 
                    cy={microcontrollerPoint.y} 
                    r={tipRadius} 
                    fill="#ffffff" 
                    stroke={color} 
                    strokeWidth={tipStroke} 
                />
                <circle
                  cx={sensorPoint.x}
                  cy={sensorPoint.y}
                  r={hitRadius}
                  fill="transparent"
                  data-wire-endpoint="1"
                  style={{ pointerEvents: 'all', cursor: disabled ? 'not-allowed' : 'grab' }}
                  onPointerDown={(event) => handleConnectionEndpointPointerDown('sensor', connection, event)}
                />
                <circle
                  cx={microcontrollerPoint.x}
                  cy={microcontrollerPoint.y}
                  r="11"
                  fill="transparent"
                  data-wire-endpoint="1"
                  style={{ pointerEvents: 'all', cursor: disabled ? 'not-allowed' : 'grab' }}
                  onPointerDown={(event) => handleConnectionEndpointPointerDown('microcontroller', connection, event)}
                />
              </g>
            );
          })}

          {draftStartPoint && draftEndPoint && (
            <g>
              <path
                d={getConnectionPath(draftStartPoint, draftEndPoint)}
                fill="none"
                stroke="#1d4ed8"
                strokeWidth={wireWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={dragPreview?.hoverTarget ? undefined : '8 6'}
                opacity="0.95"
              />
              <circle
                cx={draftStartPoint.x}
                cy={draftStartPoint.y}
                r={tipRadius}
                fill="#ffffff"
                stroke="#1d4ed8"
                strokeWidth={tipStroke}
              />

              <circle
                cx={draftEndPoint.x}
                cy={draftEndPoint.y}
                r={tipRadius + 1}
                fill="#dbeafe"
                stroke="#1d4ed8"
                strokeWidth={tipStroke}
              />
            </g>
          )}

          {/* Auto Wiring Suggestions */}
          {showAutoWiringSuggestions && autoWiringSuggestions.map((suggestion, index) => {
            const sensorPoint = pinLayout.sensor[String(suggestion.sensorPin)];
            const microcontrollerPoint = pinLayout.microcontroller[String(suggestion.microcontrollerPin)];

            if (!sensorPoint || !microcontrollerPoint) {
              return null;
            }

            const suggestionColor = suggestion.confidence === 'high' ? '#10b981' :
                                   suggestion.confidence === 'medium' ? '#f59e0b' : '#ef4444';

            return (
              <g key={`suggestion-${index}`} opacity="0.7">
                <path
                  d={getConnectionPath(sensorPoint, microcontrollerPoint)}
                  fill="none"
                  stroke={suggestionColor}
                  strokeWidth={wireWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="4 4"
                  opacity="0.8"
                />
                <circle
                  cx={sensorPoint.x}
                  cy={sensorPoint.y}
                  r={tipRadius}
                  fill="#ffffff"
                  stroke={suggestionColor}
                  strokeWidth={tipStroke}
                />
                <circle
                  cx={microcontrollerPoint.x}
                  cy={microcontrollerPoint.y}
                  r={tipRadius}
                  fill="#ffffff"
                  stroke={suggestionColor}
                  strokeWidth={tipStroke}
                />
                {/* Confidence indicator */}
                <text
                  x={(sensorPoint.x + microcontrollerPoint.x) / 2}
                  y={(sensorPoint.y + microcontrollerPoint.y) / 2 - 8}
                  textAnchor="middle"
                  fontSize="10"
                  fill={suggestionColor}
                  fontWeight="bold"
                >
                  {suggestion.confidence.toUpperCase()}
                </text>
              </g>
            );
          })}
        </Overlay>
        </WorkspaceZoomLayer>
      </Workspace>

      <ConnectionsCard>
        <ConnectionsHeader>
          <ConnectionsTitle>Connection List</ConnectionsTitle>
          <ConnectionsCount>{normalizedConnections.length} saved connection{normalizedConnections.length === 1 ? '' : 's'}</ConnectionsCount>
        </ConnectionsHeader>

        {normalizedConnections.length === 0 ? (
          <EmptyState>
            No pin connections yet. Start a wire from either board and drop it on a pinhole on the other board.
          </EmptyState>
        ) : (
          <ConnectionList>
            {renderedConnections.map(({ key, connection, sensorPin, microcontrollerPin, confidence }) => (
              <ConnectionRow key={key}>
                <ConnectionPin>
                  <ConnectionPinName>{sensorPin ? getPinDisplayName(sensorPin) : connection.sensorPin}</ConnectionPinName>
                  <ConnectionPinMeta>Sensor pin {sensorPin?.pin_number || sensorPin?.pinNumber || 'N/A'}</ConnectionPinMeta>
                </ConnectionPin>

                <ConnectionArrow>
                  <MoveRight size={16} />
                </ConnectionArrow>

                <ConnectionPin>
                  <ConnectionPinName>{microcontrollerPin ? getPinDisplayName(microcontrollerPin) : connection.microcontrollerPin}</ConnectionPinName>
                  <ConnectionPinMeta>MCU pin {microcontrollerPin?.pin_number || microcontrollerPin?.pinNumber || 'N/A'}</ConnectionPinMeta>
                </ConnectionPin>

                <RemoveButton type="button" onClick={() => removeConnection(connection)} disabled={disabled} title="Remove connection">
                  <Trash2 size={16} />
                </RemoveButton>
              </ConnectionRow>
            ))}
          </ConnectionList>
        )}
      </ConnectionsCard>

      {showAutoWiringSuggestions && autoWiringSuggestions.length > 0 && (
        <ConnectionsCard>
          <ConnectionsHeader>
            <ConnectionsTitle>Suggested Wiring</ConnectionsTitle>
            <ConnectionsCount>{autoWiringSuggestions.length} suggestion{autoWiringSuggestions.length === 1 ? '' : 's'}</ConnectionsCount>
          </ConnectionsHeader>

          <ConnectionList>
            {autoWiringSuggestions.map((suggestion, index) => {
              const sensorPin = sensorPinsById.get(String(suggestion.sensorPin));
              const microcontrollerPin = microcontrollerPinsById.get(String(suggestion.microcontrollerPin));

              const confidenceColor = suggestion.confidence === 'high' ? '#10b981' :
                                     suggestion.confidence === 'medium' ? '#f59e0b' : '#ef4444';

              return (
                <ConnectionRow key={`suggestion-${index}`}>
                  <ConnectionPin>
                    <ConnectionPinName>{sensorPin ? getPinDisplayName(sensorPin) : suggestion.sensorPin}</ConnectionPinName>
                    <ConnectionPinMeta>Sensor pin {sensorPin?.pin_number || sensorPin?.pinNumber || 'N/A'}</ConnectionPinMeta>
                  </ConnectionPin>

                  <ConnectionArrow>
                    <MoveRight size={16} />
                  </ConnectionArrow>

                  <ConnectionPin>
                    <ConnectionPinName>{microcontrollerPin ? getPinDisplayName(microcontrollerPin) : suggestion.microcontrollerPin}</ConnectionPinName>
                    <ConnectionPinMeta>MCU pin {microcontrollerPin?.pin_number || microcontrollerPin?.pinNumber || 'N/A'}</ConnectionPinMeta>
                  </ConnectionPin>

                  <div style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    background: confidenceColor,
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    minWidth: '60px'
                  }}>
                    {suggestion.confidence.toUpperCase()}
                  </div>
                </ConnectionRow>
              );
            })}
          </ConnectionList>
        </ConnectionsCard>
      )}

    </EditorShell>
  );
};

export default WiringConnectionEditor;
